import { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { db } from "../lib/db";
import { apiFetch } from "../lib/api";
import { getCurrentProvider } from "../lib/currentProvider";
import { useApp } from "../context/AppContext";
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

// When staff reply to a portal message, text the patient a generic nudge —
// "you have a new message in your portal" — so the conversation stays in the
// portal (our infrastructure) and the SMS itself carries zero PHI (no name,
// no clinical content). This is what keeps Telnyx BAA-free. Fire-and-forget:
// nudge failure must never block the reply (send-sms 412s when comms are off).
async function sendPortalNudge(patientId) {
  if (isDemoMode) return;
  try {
    if (!patientId || !/^[0-9a-f-]{36}$/.test(patientId)) return;
    const { data } = await db.from("patients").select("phone").eq("id", patientId);
    const phone = data?.[0]?.phone;
    if (!phone) return;
    await apiFetch("send-sms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        toNumber: phone,
        messageType: "general",
        patientId,
        message: "You have a new message from your care team in your Epiphany MD patient portal: https://epiphanymd.com/portal — Reply STOP to opt out.",
      }),
    });
  } catch { /* opportunistic — never surface */ }
}

// portal_messages columns the inbox is allowed to UPDATE (must match the
// db-query allowlist PORTAL_MESSAGES_UPDATE_COLUMNS). The in-memory message
// object carries derived fields (`type` aliasing message_type, `patient_name`,
// `status`) that are NOT real columns — strip to the writable set before update.
const PORTAL_MESSAGE_WRITABLE = [
  'is_read', 'read_at', 'assigned_to', 'flagged_urgent', 'flag_reason',
  'responded_at', 'responded_by', 'message_type',
];

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  navy:     "var(--navy)",
  navy2:    "var(--navy-2)",
  navy3:    "var(--navy-3)",
  navy4:    "var(--navy-4)",
  teal:     "#0ea5a0",
  tealLt:   "rgba(14,165,160,0.12)",
  amber:    "#f0a500",
  amberLt:  "rgba(240,165,0,0.12)",
  red:      "#e05252",
  redLt:    "rgba(224,82,82,0.12)",
  green:    "#22c55e",
  greenLt:  "rgba(34,197,94,0.12)",
  blue:     "#3b82f6",
  blueLt:   "rgba(59,130,246,0.12)",
  purple:   "#a855f7",
  purpleLt: "rgba(168,85,247,0.12)",
  text1:    "var(--text-1)",
  text2:    "var(--text-2)",
  text3:    "var(--text-3)",
  border:   "var(--border)",
  border2:  "var(--border-2)",
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const MSG_TYPES = [
  { id: "general",         label: "General Question",        icon: "🔵", color: C.teal,   bgColor: C.tealLt,   autoAssign: null          },
  { id: "medication-refill", label: "Medication Refill",     icon: "💊", color: C.amber,  bgColor: C.amberLt,  autoAssign: "ma-team"     },
  { id: "appointment",     label: "Appointment Request",     icon: "📅", color: C.blue,   bgColor: C.blueLt,   autoAssign: "front-desk"  },
  { id: "test-results",    label: "Test Results Question",   icon: "🔬", color: C.purple, bgColor: C.purpleLt, autoAssign: null          },
  { id: "referral",        label: "Referral Request",        icon: "📋", color: C.teal,   bgColor: C.tealLt,   autoAssign: "front-desk"  },
  { id: "billing",         label: "Billing Question",        icon: "💰", color: C.green,  bgColor: C.greenLt,  autoAssign: "sarah-m"     },
  { id: "post-op-concern", label: "Post-Op Concern",         icon: "🩹", color: C.red,    bgColor: C.redLt,    autoAssign: "dr-walker"   },
  { id: "new-symptom",     label: "New Symptom",             icon: "🚨", color: C.red,    bgColor: C.redLt,    autoAssign: "dr-walker"   },
];

const STAFF_LIST = [
  { id: "dr-walker",  name: "Dr. R. Walker",         role: "Physician",         initials: "RW", color: C.teal     },
  { id: "ma-team",    name: "MA Team",                role: "Medical Assistant", initials: "MA", color: "#14b8a6"  },
  { id: "sarah-m",    name: "Sarah M.",               role: "Billing",           initials: "SM", color: "#ec4899"  },
  { id: "james-t",    name: "James T.",               role: "Billing",           initials: "JT", color: "#10b981"  },
  { id: "lisa-k",     name: "Lisa K.",                role: "Billing",           initials: "LK", color: "#6366f1"  },
  { id: "front-desk", name: "Front Desk",             role: "Admin",             initials: "FD", color: "#f97316"  },
  { id: "practice",   name: "Epiphany MD Care Team",  role: "Practice",          initials: "EP", color: C.teal     },
];

const CLINICAL_DISCLAIMER = `─────────────────────────────────────────
IMPORTANT: This response is based on the information provided in your message. Without an in-person clinical examination, the complete clinical picture cannot be fully appreciated. This communication does not constitute a clinical evaluation or establish a physician-patient relationship for this specific concern. If your symptoms worsen or you have any concerns, please call our office at (480) 555-0100 or seek in-person evaluation.
─────────────────────────────────────────`;

const MOCK_PT_MEDS = {
  P001: ["Meloxicam 15mg daily", "Omeprazole 20mg daily", "Aspirin 81mg daily", "Gabapentin 300mg TID", "Cyclobenzaprine 5mg PRN"],
  P002: ["Meloxicam 15mg daily", "Metformin 1000mg BID", "Lisinopril 10mg daily", "Atorvastatin 40mg nightly"],
  P003: ["Ibuprofen 600mg TID PRN", "Hydrocodone/APAP 5/325 PRN", "Baclofen 10mg TID"],
  P004: ["Metoprolol 50mg daily", "Amlodipine 5mg daily", "Furosemide 20mg daily", "Aspirin 81mg daily", "Celecoxib 200mg daily"],
  P005: ["Ibuprofen 800mg TID PRN", "Gabapentin 600mg TID", "Prednisone 10mg taper"],
};

const MOCK_PT_NOTES = {
  P001: [
    { date: "2026-03-10", type: "Post-Op Visit",   summary: "Wound healing well. ROM improving. Continue PT 3x/week." },
    { date: "2026-02-20", type: "Operative Note",  summary: "Right rotator cuff repair, arthroscopic. No intraop complications." },
  ],
  P002: [
    { date: "2026-03-15", type: "Office Visit",    summary: "Meloxicam refill requested. NSAID monitoring labs reviewed." },
    { date: "2026-02-28", type: "Office Visit",    summary: "Knee pain follow-up. Cortisone injection administered right knee." },
  ],
  P003: [
    { date: "2026-03-18", type: "Post-Op Visit",   summary: "Trigger finger release 6-week check. Full ROM achieved." },
    { date: "2026-02-05", type: "Operative Note",  summary: "Left trigger finger release. Uncomplicated." },
  ],
  P004: [
    { date: "2026-03-20", type: "Pre-Op Visit",    summary: "Pre-op evaluation for CMC arthroplasty. Cleared pending EKG." },
    { date: "2026-03-01", type: "Office Visit",    summary: "Right thumb CMC arthritis. Discussed surgical options." },
  ],
  P005: [
    { date: "2026-03-22", type: "Post-Op Visit",   summary: "Post-op nerve symptoms reported. EMG ordered." },
    { date: "2026-03-05", type: "Operative Note",  summary: "Right carpal tunnel release. No complications." },
  ],
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const dAgo = (days) => { const d = new Date(); d.setTime(d.getTime() - days * 86400000); return d.toISOString(); };
const hAgo = (hours) => { const d = new Date(); d.setTime(d.getTime() - hours * 3600000); return d.toISOString(); };
const mAgo = (mins)  => { const d = new Date(); d.setTime(d.getTime() - mins  * 60000);   return d.toISOString(); };

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), n = new Date();
  if (d.toDateString() === n.toDateString())
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const diff = Math.floor((n - d) / 86400000);
  if (diff < 7) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const fmtFull = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
};

const fmtDob = (dob) => {
  if (!dob) return "";
  const [y, mo, day] = dob.split("-");
  const d = new Date(+y, +mo - 1, +day);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const calcAge = (dob) => {
  if (!dob) return 0;
  const [y, mo, day] = dob.split("-").map(Number);
  const today = new Date(), birth = new Date(y, mo - 1, day);
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) age--;
  return age;
};

const getInitials = (name) =>
  name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?";

// Safely extract a display string from either a plain string or a Supabase record object
const strVal = (v) => typeof v === "string" ? v : (v?.name || v?.text || v?.description || v?.substance || "");

const isOverdue = (msg) => {
  if (!msg) return false;
  if (msg.status === "resolved" || msg.status === "archived") return false;
  if (msg.responded_at) return false;
  const created = new Date(msg.created_at);
  return (Date.now() - created.getTime()) > 86400000;
};

const getMsgType = (typeId) =>
  MSG_TYPES.find(t => t.id === typeId) || {
    id: typeId, label: typeId, icon: "💬", color: C.text2, bgColor: C.navy3, autoAssign: null,
  };

const getStaff = (id) => STAFF_LIST.find(s => s.id === id) || { id, name: id, role: "", initials: getInitials(id || "?"), color: C.text3 };

// ─── MOCK PORTAL INBOX ────────────────────────────────────────────────────────
export const MOCK_PORTAL_INBOX = [
  // 1. General Question — James Hartley, next appointment
  {
    id: "PM001",
    patient_id: "P001",
    patient_name: "James Hartley",
    mrn: "OC-10042",
    dob: "1968-04-15",
    sender_type: "patient",
    subject: "Question About My Next Appointment",
    body: "Hi, I just wanted to confirm my next appointment. I believe it's sometime next month but I can't find the paperwork. Can you let me know the date and time? Also, do I need to bring anything specific or do any prep beforehand? Thank you.",
    type: "general",
    assigned_to: null,
    is_read: false,
    flagged_urgent: false,
    flag_reason: null,
    status: "open",
    created_at: hAgo(4),
    responded_at: null,
    responded_by: null,
    replies: [],
  },
  // 2. Medication Refill — Maria Delgado, oxycodone
  {
    id: "PM002",
    patient_id: "P002",
    patient_name: "Maria Delgado",
    mrn: "OC-10043",
    dob: "1982-09-22",
    sender_type: "patient",
    subject: "Medication Refill Request: Oxycodone",
    body: "Hello, I need a refill of my oxycodone prescription. I have about 3 days left and I don't want to run out over the weekend. My pharmacy is CVS on Central Ave. Can you please send the refill over as soon as possible? Thank you.",
    type: "medication-refill",
    assigned_to: "ma-team",
    is_read: false,
    flagged_urgent: false,
    flag_reason: null,
    status: "open",
    created_at: hAgo(6),
    responded_at: null,
    responded_by: null,
    replies: [],
  },
  // 3. Appointment Request — Robert Chen, follow-up
  {
    id: "PM003",
    patient_id: "P003",
    patient_name: "Robert Chen",
    mrn: "OC-10044",
    dob: "1975-12-08",
    sender_type: "patient",
    subject: "Schedule Follow-Up Appointment",
    body: "Hi, I'd like to schedule a follow-up appointment after my trigger finger surgery. I was told to come back in about 6 weeks and that time is coming up. I'm available Monday through Wednesday in the mornings. Please let me know what's open. Thank you.",
    type: "appointment",
    assigned_to: "front-desk",
    is_read: false,
    flagged_urgent: false,
    flag_reason: null,
    status: "open",
    created_at: hAgo(10),
    responded_at: null,
    responded_by: null,
    replies: [],
  },
  // 4. Test Results — Patricia Monroe, lab results
  {
    id: "PM004",
    patient_id: "P004",
    patient_name: "Patricia Monroe",
    mrn: "OC-10045",
    dob: "1959-07-03",
    sender_type: "patient",
    subject: "Question About My Recent Lab Results",
    body: "I received a notification that my lab results are ready but I can't see them in the portal. I had blood work done last Tuesday — a metabolic panel and CBC. Can someone tell me if everything looks normal? I'm particularly concerned about my kidney function since I've been on Celecoxib for a while. Thank you.",
    type: "test-results",
    assigned_to: null,
    is_read: false,
    flagged_urgent: false,
    flag_reason: null,
    status: "open",
    created_at: dAgo(1),
    responded_at: null,
    responded_by: null,
    replies: [],
  },
  // 5. Referral Request — David Okafor, PT
  {
    id: "PM005",
    patient_id: "P005",
    patient_name: "David Okafor",
    mrn: "OC-10046",
    dob: "1990-02-17",
    sender_type: "patient",
    subject: "PT Referral Request",
    body: "Hello, Dr. Walker mentioned I should start physical therapy after my carpal tunnel release. I'd like to go to Desert Hand and Physical Therapy on Scottsdale Road — my coworker goes there and says they're great. Can you send a referral? Their number is (480) 555-0190. Thank you.",
    type: "referral",
    assigned_to: "front-desk",
    is_read: false,
    flagged_urgent: false,
    flag_reason: null,
    status: "open",
    created_at: dAgo(1.5),
    responded_at: null,
    responded_by: null,
    replies: [],
  },
  // 6. Billing Question — James Hartley, statement
  {
    id: "PM006",
    patient_id: "P001",
    patient_name: "James Hartley",
    mrn: "OC-10042",
    dob: "1968-04-15",
    sender_type: "patient",
    subject: "Billing Statement Question",
    body: "I received a statement in the mail for $248.00 from my last visit on March 10th. I thought my insurance covered everything after my deductible was met in January. Can someone look into this? I don't think I should owe this amount. My BCBS member number is on file. Thanks.",
    type: "billing",
    assigned_to: "sarah-m",
    is_read: false,
    flagged_urgent: false,
    flag_reason: null,
    status: "open",
    created_at: dAgo(2),
    responded_at: null,
    responded_by: null,
    replies: [],
  },
  // 7. Post-Op Concern — Maria Delgado, increased swelling
  {
    id: "PM007",
    patient_id: "P002",
    patient_name: "Maria Delgado",
    mrn: "OC-10043",
    dob: "1982-09-22",
    sender_type: "patient",
    subject: "Post-Op Concern: Increased Swelling",
    body: "Hi, I had my knee surgery two weeks ago and the swelling has actually gotten worse over the last two days, not better. My knee is very puffy and feels tight. I've been doing the ice and elevation like instructed. The incision doesn't look red but the whole knee is swollen. Should I come in? Is this normal at this stage?",
    type: "post-op-concern",
    assigned_to: "dr-walker",
    is_read: false,
    flagged_urgent: true,
    flag_reason: "Worsening post-op swelling at 2 weeks",
    status: "open",
    created_at: dAgo(0.3),
    responded_at: null,
    responded_by: null,
    replies: [],
  },
  // 8. New Symptom — Robert Chen, numbness in fingers
  {
    id: "PM008",
    patient_id: "P003",
    patient_name: "Robert Chen",
    mrn: "OC-10044",
    dob: "1975-12-08",
    sender_type: "patient",
    subject: "New Symptom: Numbness in Fingers",
    body: "Hello, I've been noticing a new symptom over the past week that has me concerned. My ring finger and pinky on my right hand have been going numb, especially when I bend my elbow or sleep. It wasn't like this before my trigger finger surgery. Could this be related? It's starting to affect my ability to type at work.",
    type: "new-symptom",
    assigned_to: "dr-walker",
    is_read: false,
    flagged_urgent: false,
    flag_reason: null,
    status: "open",
    created_at: dAgo(2.8),
    responded_at: null,
    responded_by: null,
    replies: [],
  },
];

// ─── EXPORT HELPER ────────────────────────────────────────────────────────────
export const getPortalUnreadCount = (messages) =>
  (messages || []).filter(m => !m.is_read && m.status !== "archived").length;

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

function MsgTypeBadge({ type, small = false }) {
  const cfg = getMsgType(type);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: cfg.bgColor, color: cfg.color,
      borderRadius: 4,
      fontSize: small ? 9 : 10,
      padding: small ? "1px 3px" : "2px 6px",
      fontWeight: 600, letterSpacing: "0.02em",
      whiteSpace: "nowrap",
    }}>
      <span style={{ fontSize: small ? 9 : 10 }}>{cfg.icon}</span>
      {cfg.label}
    </span>
  );
}

function StaffAvatar({ staffId, size = 28 }) {
  const s = getStaff(staffId);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: s.color + "30", border: `1.5px solid ${s.color}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.36, fontWeight: 700, color: s.color,
      flexShrink: 0, userSelect: "none",
    }}>
      {s.initials}
    </div>
  );
}

function PatAvatar({ name, size = 28 }) {
  const initials = getInitials(name || "?");
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: C.tealLt, border: `1.5px solid ${C.teal}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.36, fontWeight: 700, color: C.teal,
      flexShrink: 0, userSelect: "none",
    }}>
      {initials}
    </div>
  );
}

function InboxRow({ msg, selected, onClick }) {
  const [hovered, setHovered] = useState(false);
  const overdue = isOverdue(msg);
  const assignedStaff = msg.assigned_to ? getStaff(msg.assigned_to) : null;

  const borderColor = msg.flagged_urgent
    ? C.red
    : overdue
    ? C.amber
    : selected
    ? C.teal
    : "transparent";

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", gap: 10, padding: "10px 12px",
        background: selected ? C.navy3 : hovered ? C.navy2 : "transparent",
        borderLeft: `3px solid ${borderColor}`,
        cursor: "pointer", transition: "background 0.15s",
      }}
    >
      <PatAvatar name={msg.patient_name} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Row 1 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 4, marginBottom: 2 }}>
          <span style={{
            fontSize: 13, fontWeight: msg.is_read ? 500 : 700,
            color: msg.is_read ? C.text1 : "#fff",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {msg.patient_name}
          </span>
          <span style={{ fontSize: 10, color: C.text3, flexShrink: 0 }}>{fmtTime(msg.created_at)}</span>
        </div>
        {/* Row 2 */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
          {!msg.is_read && (
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.blue, flexShrink: 0 }} />
          )}
          <span style={{
            fontSize: 12, color: C.text2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            flex: 1,
          }}>
            {msg.subject}
          </span>
        </div>
        {/* Row 3 */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
          <MsgTypeBadge type={msg.type} small />
          {overdue && (
            <span style={{
              fontSize: 9, fontWeight: 700, background: C.amberLt, color: C.amber,
              borderRadius: 3, padding: "1px 4px",
            }}>OVERDUE</span>
          )}
          {msg.flagged_urgent && (
            <span style={{
              fontSize: 9, fontWeight: 700, background: C.redLt, color: C.red,
              borderRadius: 3, padding: "1px 4px",
            }}>URGENT</span>
          )}
          {msg.status === "resolved" && (
            <span style={{
              fontSize: 9, fontWeight: 700, background: C.greenLt, color: C.green,
              borderRadius: 3, padding: "1px 4px",
            }}>✓ Resolved</span>
          )}
          <div style={{ flex: 1 }} />
          {assignedStaff && (
            <div title={assignedStaff.name} style={{
              width: 18, height: 18, borderRadius: "50%",
              background: assignedStaff.color + "30", border: `1.5px solid ${assignedStaff.color}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 8, fontWeight: 700, color: assignedStaff.color,
            }}>
              {assignedStaff.initials}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadView({ thread, patients, onUpdate }) {
  const [replyBody, setReplyBody]         = useState("");
  const [respondAs, setRespondAs]         = useState("dr-walker");
  const [includeDisclaimer, setInclude]   = useState(true);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagReason, setFlagReason]       = useState("");
  const bodyRef = useRef(null);

  useEffect(() => {
    setReplyBody("");
    setInclude(true);
    setShowFlagModal(false);
    setFlagReason("");
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [thread?.id]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [thread?.replies?.length]);

  if (!thread) {
    return (
      <div style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        background: C.navy, color: C.text3, fontSize: 14,
        flexDirection: "column", gap: 10,
      }}>
        <div style={{ fontSize: 36 }}>💬</div>
        <div>Select a message to view the thread</div>
      </div>
    );
  }

  const overdue = isOverdue(thread);
  const staff = STAFF_LIST.filter(s => s.id !== "practice");

  const handleSend = () => {
    if (!replyBody.trim() || replyBody.length > 2000) return;
    const s = getStaff(respondAs);
    const newReply = {
      id: `R${thread.id}-${Date.now()}`,
      body: replyBody.trim(),
      sender_id: respondAs,
      sender_name: s.name,
      sender_type: "staff",
      created_at: new Date().toISOString(),
      include_disclaimer: includeDisclaimer,
    };
    const updated = {
      ...thread,
      replies: [...(thread.replies || []), newReply],
      responded_at: newReply.created_at,
      responded_by: respondAs,
    };
    onUpdate(updated);
    setReplyBody("");
    sendPortalNudge(thread.patient_id); // generic "check your portal" SMS — no PHI
  };

  const handleResolve = () => onUpdate({ ...thread, status: "resolved" });

  const handleFlagSubmit = () => {
    if (!flagReason.trim()) return;
    onUpdate({ ...thread, flagged_urgent: true, flag_reason: flagReason.trim() });
    setShowFlagModal(false);
    setFlagReason("");
  };

  const charCount = replyBody.length;

  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      background: C.navy, overflow: "hidden", position: "relative",
    }}>
      {/* Header */}
      <div style={{
        padding: "12px 16px", borderBottom: `1px solid ${C.border}`,
        background: C.navy2, flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <PatAvatar name={thread.patient_name} size={36} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text1 }}>{thread.patient_name}</div>
              <div style={{ fontSize: 11, color: C.text3, marginTop: 1 }}>
                DOB: {fmtDob(thread.dob)}{thread.dob ? ` (${calcAge(thread.dob)} y/o)` : ""} &nbsp;·&nbsp; MRN: {thread.mrn}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            {!thread.flagged_urgent && (
              <button
                onClick={() => setShowFlagModal(true)}
                style={{
                  padding: "5px 10px", borderRadius: 5, border: `1px solid ${C.red}`,
                  background: C.redLt, color: C.red, fontSize: 11, fontWeight: 600, cursor: "pointer",
                }}
              >🚩 Flag Urgent</button>
            )}
            {thread.status !== "resolved" && (
              <button
                onClick={handleResolve}
                style={{
                  padding: "5px 10px", borderRadius: 5, border: `1px solid ${C.green}`,
                  background: C.greenLt, color: C.green, fontSize: 11, fontWeight: 600, cursor: "pointer",
                }}
              >✓ Resolve</button>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MsgTypeBadge type={thread.type} />
          <span style={{ fontSize: 13, color: C.text1, fontWeight: 600 }}>{thread.subject}</span>
          {thread.flagged_urgent && (
            <span style={{ fontSize: 10, fontWeight: 700, background: C.redLt, color: C.red, borderRadius: 3, padding: "2px 6px" }}>
              URGENT
            </span>
          )}
          {thread.status === "resolved" && (
            <span style={{ fontSize: 10, fontWeight: 700, background: C.greenLt, color: C.green, borderRadius: 3, padding: "2px 6px" }}>
              ✓ Resolved
            </span>
          )}
          {overdue && (
            <span style={{ fontSize: 10, fontWeight: 700, background: C.amberLt, color: C.amber, borderRadius: 3, padding: "2px 6px" }}>
              OVERDUE
            </span>
          )}
        </div>
      </div>

      {/* Clinical warning banner */}
      <div style={{
        padding: "7px 16px", background: C.amberLt,
        borderBottom: `1px solid rgba(240,165,0,0.2)`,
        fontSize: 11, color: C.amber, flexShrink: 0,
      }}>
        ⚠ Reminder: For clinical questions, responses are based on limited information and a portal message does not substitute for an in-person clinical examination.
      </div>

      {/* Message body / thread */}
      <div ref={bodyRef} style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        {/* Original patient message */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16, maxWidth: "80%" }}>
          <PatAvatar name={thread.patient_name} size={24} />
          <div>
            <div style={{
              background: C.navy3, border: `1px solid ${C.border2}`,
              borderRadius: "0 10px 10px 10px", padding: "10px 12px",
            }}>
              <div style={{ fontSize: 11, color: C.text3, marginBottom: 6 }}>
                {thread.patient_name} &nbsp;·&nbsp; {fmtFull(thread.created_at)}
              </div>
              <div style={{ fontSize: 13, color: C.text1, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                {thread.body}
              </div>
            </div>
          </div>
        </div>

        {/* Replies */}
        {(thread.replies || []).map(reply => {
          // System log entries render as a centered pill, not a chat bubble
          if (reply.is_log) {
            return (
              <div key={reply.id} style={{ textAlign: "center", marginBottom: 12 }}>
                <span style={{
                  fontSize: 10, color: C.text3, background: C.navy3,
                  border: `1px solid ${C.border}`, borderRadius: 20,
                  padding: "3px 10px", fontStyle: "italic", display: "inline-block",
                }}>
                  📋 {reply.body}
                </span>
              </div>
            );
          }
          const isStaff = reply.sender_type === "staff";
          return (
            <div key={reply.id} style={{
              display: "flex", gap: 10, marginBottom: 16,
              flexDirection: isStaff ? "row-reverse" : "row",
              maxWidth: "80%", marginLeft: isStaff ? "auto" : 0,
            }}>
              {isStaff
                ? <StaffAvatar staffId={reply.sender_id} size={24} />
                : <PatAvatar name={reply.sender_name} size={24} />
              }
              <div style={{ maxWidth: "100%" }}>
                <div style={{
                  background: isStaff ? C.tealLt : C.navy3,
                  border: `1px solid ${isStaff ? "rgba(14,165,160,0.3)" : C.border2}`,
                  borderRadius: isStaff ? "10px 0 10px 10px" : "0 10px 10px 10px",
                  padding: "10px 12px",
                }}>
                  <div style={{ fontSize: 11, color: C.text3, marginBottom: 6, textAlign: isStaff ? "right" : "left" }}>
                    {reply.sender_name} &nbsp;·&nbsp; {fmtFull(reply.created_at)}
                  </div>
                  <div style={{ fontSize: 13, color: C.text1, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                    {reply.body}
                  </div>
                  {reply.include_disclaimer && (
                    <div style={{
                      marginTop: 10, fontSize: 10, color: C.text3,
                      fontStyle: "italic", whiteSpace: "pre-wrap", lineHeight: 1.5,
                    }}>
                      {CLINICAL_DISCLAIMER}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Refill action card — only for medication-refill messages */}
      {thread.type === "medication-refill" && thread.status !== "resolved" && (
        <RefillActionCard thread={thread} onUpdate={onUpdate} />
      )}

      {/* Reply box */}
      {thread.status !== "resolved" && (
        <div style={{
          borderTop: `1px solid ${C.border}`, padding: "12px 16px",
          background: C.navy2, flexShrink: 0,
        }}>
          {/* Controls row */}
          <div style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center" }}>
            <label style={{ fontSize: 11, color: C.text3, whiteSpace: "nowrap" }}>Respond as</label>
            <select
              value={respondAs}
              onChange={e => setRespondAs(e.target.value)}
              style={{
                background: C.navy3, border: `1px solid ${C.border2}`,
                color: C.text1, borderRadius: 5, fontSize: 12,
                padding: "4px 8px", outline: "none", cursor: "pointer",
              }}
            >
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
              ))}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.text2, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={includeDisclaimer}
                onChange={e => setInclude(e.target.checked)}
                style={{ accentColor: C.teal }}
              />
              Clinical Disclaimer
            </label>
          </div>

          <textarea
            rows={4}
            placeholder="Type your reply..."
            value={replyBody}
            onChange={e => setReplyBody(e.target.value)}
            style={{
              width: "100%", boxSizing: "border-box",
              background: C.navy3, border: `1px solid ${C.border2}`,
              borderRadius: 6, color: C.text1, fontSize: 13,
              padding: "8px 10px", outline: "none", resize: "vertical",
              lineHeight: 1.5, fontFamily: "inherit",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={handleResolve}
                style={{
                  padding: "6px 12px", borderRadius: 5, border: `1px solid ${C.green}`,
                  background: C.greenLt, color: C.green, fontSize: 11, fontWeight: 600, cursor: "pointer",
                }}
              >✓ Mark Resolved</button>
              {!thread.flagged_urgent && (
                <button
                  onClick={() => setShowFlagModal(true)}
                  style={{
                    padding: "6px 12px", borderRadius: 5, border: `1px solid ${C.red}`,
                    background: C.redLt, color: C.red, fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}
                >🚩 Flag Urgent</button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: charCount > 1900 ? C.red : C.text3 }}>
                {charCount}/2000
              </span>
              <button
                onClick={handleSend}
                disabled={!replyBody.trim() || charCount > 2000}
                style={{
                  padding: "6px 16px", borderRadius: 5,
                  background: (!replyBody.trim() || charCount > 2000) ? C.navy4 : C.teal,
                  color: (!replyBody.trim() || charCount > 2000) ? C.text3 : "#fff",
                  border: "none", fontSize: 12, fontWeight: 600, cursor:
                    (!replyBody.trim() || charCount > 2000) ? "not-allowed" : "pointer",
                  transition: "background 0.15s",
                }}
              >Send Reply</button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Urgent Modal */}
      {showFlagModal && (
        <div style={{
          position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 100,
        }}>
          <div style={{
            background: C.navy2, border: `1px solid ${C.border2}`,
            borderRadius: 10, padding: 20, width: 340,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text1, marginBottom: 4 }}>
              🚩 Flag as Urgent
            </div>
            <div style={{ fontSize: 12, color: C.text2, marginBottom: 12 }}>
              Describe the reason this message should be flagged as urgent:
            </div>
            <textarea
              rows={3}
              placeholder="e.g. Post-op wound concern, severe pain..."
              value={flagReason}
              onChange={e => setFlagReason(e.target.value)}
              style={{
                width: "100%", boxSizing: "border-box",
                background: C.navy3, border: `1px solid ${C.border2}`,
                borderRadius: 6, color: C.text1, fontSize: 13,
                padding: "8px 10px", outline: "none", resize: "vertical",
                lineHeight: 1.5, fontFamily: "inherit", marginBottom: 12,
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={() => { setShowFlagModal(false); setFlagReason(""); }}
                style={{
                  padding: "6px 14px", borderRadius: 5,
                  background: C.navy4, border: `1px solid ${C.border2}`,
                  color: C.text2, fontSize: 12, cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={handleFlagSubmit}
                disabled={!flagReason.trim()}
                style={{
                  padding: "6px 14px", borderRadius: 5,
                  background: flagReason.trim() ? C.red : C.navy4,
                  border: "none", color: flagReason.trim() ? "#fff" : C.text3,
                  fontSize: 12, fontWeight: 600,
                  cursor: flagReason.trim() ? "pointer" : "not-allowed",
                }}
              >🚩 Flag Urgent</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── REFILL ACTION CONSTANTS ──────────────────────────────────────────────────
const CONTROLLED_KEYWORDS = [
  "oxycodone","hydrocodone","morphine","fentanyl","tramadol",
  "benzodiazepine","xanax","valium","klonopin","adderall",
  "ritalin","oxycontin","percocet","vicodin","dilaudid",
  "codeine","methadone","buprenorphine","suboxone",
];

const DENY_REASONS = [
  "Too soon to refill",
  "Controlled substance — requires office visit",
  "Medication discontinued",
  "Requires updated prescription",
  "Insurance requires prior authorization",
  "Provider not available — please call office",
  "Other",
];

function parseMedName(subject) {
  const m = (subject || "").match(/refill(?:\s+request)?[:\-]\s*(.+)/i);
  return m ? m[1].trim() : "your medication";
}

function parsePharmacy(body) {
  const m = (body || "").match(/(CVS[^.,\n]*|Walgreens[^.,\n]*|Rite Aid[^.,\n]*|Walmart[^.,\n]*|Costco[^.,\n]*|pharmacy\s+(?:is\s+)?(?:at\s+)?[^.,\n]+)/i);
  return m ? m[1].trim().replace(/^(pharmacy is |pharmacy at )/i, "") : "Pharmacy on file";
}

function isControlledSubstance(medName) {
  const lower = (medName || "").toLowerCase();
  return CONTROLLED_KEYWORDS.some(k => lower.includes(k));
}

// ─── REFILL ACTION CARD ───────────────────────────────────────────────────────
function RefillActionCard({ thread, onUpdate }) {
  const initMed = parseMedName(thread.subject);
  const initPha = parsePharmacy(thread.body);

  const [panel,       setPanel]       = useState(null); // null|'approve'|'deny'|'modify'
  const [pdmpChecked, setPdmpChecked] = useState(false);
  // approve
  const [aMed,     setAMed]     = useState(initMed);
  const [aQty,     setAQty]     = useState("30");
  const [aRefills, setARefills] = useState("1");
  const [aPharma,  setAPharma]  = useState(initPha);
  // deny
  const [dReason, setDReason] = useState(DENY_REASONS[0]);
  const [dNote,   setDNote]   = useState("");
  // modify
  const [mDrug,     setMDrug]     = useState(initMed);
  const [mStrength, setMStrength] = useState("");
  const [mSig,      setMSig]      = useState("");
  const [mQty,      setMQty]      = useState("30");
  const [mRefills,  setMRefills]  = useState("1");
  const [mPharma,   setMPharma]   = useState(initPha);
  const [mNote,     setMNote]     = useState("");
  const [mDaw,      setMDaw]      = useState(false);

  useEffect(() => {
    const m = parseMedName(thread.subject);
    const p = parsePharmacy(thread.body);
    setPanel(null); setPdmpChecked(false);
    setAMed(m); setAQty("30"); setARefills("1"); setAPharma(p);
    setDReason(DENY_REASONS[0]); setDNote("");
    setMDrug(m); setMStrength(""); setMSig(""); setMQty("30");
    setMRefills("1"); setMPharma(p); setMNote(""); setMDaw(false);
  }, [thread.id]);

  const med       = parseMedName(thread.subject);
  const controlled = isControlledSubstance(med);
  const canApprove = !controlled || pdmpChecked;

  const nowStr = new Date().toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
  const PROVIDER = "Dr. R. Walker";
  const OFFICE_PHONE = "(480) 555-0100";

  function commit(autoBody, logBody, rxData) {
    const ts = new Date().toISOString();
    if (rxData) {
      (async () => { try { await db.from("prescriptions").insert({ ...rxData, patient_id: thread.patient_id, created_at: ts, source: "portal_message", source_id: thread.id }); } catch {} })();
    }
    // This insert carried `created_by: "dr-walker"` — a literal — and NO
    // `provider` at all, so a refill-authorization note had no real attribution
    // anywhere. Now it writes the canonical `provider` column that every other
    // note uses, resolved from the signed-in user.
    //
    // OMITTED rather than defaulted when unresolved: this insert sits inside a
    // silent catch, so a blocking guard would fail invisibly. An absent
    // attribution is honest; a wrong one is not. created_by is dropped — the web
    // sets it nowhere else on clinical_notes, and it has no agreed meaning here.
    (async () => { try {
      const cp = await getCurrentProvider();
      const providerName = cp
        ? `Dr. ${`${cp.first_name || ''} ${cp.last_name || ''}`.trim()}`.trim()
        : '';
      await db.from("clinical_notes").insert({
        patient_id: thread.patient_id,
        note_type: "Refill Authorization",
        body: logBody,
        created_at: ts,
        ...(providerName ? { provider: providerName } : {}),
      });
    } catch {} })();
    const autoReply = {
      id: `R${thread.id}-ar-${Date.now()}`,
      body: autoBody,
      sender_id: "practice",
      sender_name: "Epiphany MD Care Team",
      sender_type: "staff",
      created_at: ts,
      include_disclaimer: false,
    };
    const logEntry = {
      id: `R${thread.id}-log-${Date.now() + 1}`,
      body: logBody,
      sender_id: "system",
      sender_name: "System",
      sender_type: "system",
      created_at: ts,
      include_disclaimer: false,
      is_log: true,
    };
    onUpdate({
      ...thread,
      status: "resolved",
      responded_at: ts,
      responded_by: "dr-walker",
      replies: [...(thread.replies || []), autoReply, logEntry],
    });
    setPanel(null);
  }

  function handleApprove() {
    const autoBody = `Your refill request for ${aMed} has been approved and sent to ${aPharma}. Please allow 24–48 hours for processing. If you have questions please call our office at ${OFFICE_PHONE}.`;
    const logBody  = `Refill approved for ${aMed} via patient portal message on ${nowStr}. Qty: ${aQty}, Refills: ${aRefills}, Pharmacy: ${aPharma}. Authorized by ${PROVIDER}.`;
    commit(autoBody, logBody, { medication: aMed, quantity: aQty, refills: aRefills, pharmacy: aPharma, status: "active", prescribed_by: "dr-walker" });
  }

  function handleDeny() {
    const reason = dReason === "Other" ? (dNote.trim() || "See office") : dReason;
    const extra  = dReason !== "Other" && dNote.trim() ? ` ${dNote.trim()}` : "";
    const autoBody = `Your refill request for ${med} has not been approved at this time. Reason: ${reason}.${extra} Please call our office at ${OFFICE_PHONE} if you have questions.`;
    const logBody  = `Refill denied for ${med} via patient portal message on ${nowStr}. Reason: ${reason}.${extra} Authorized by ${PROVIDER}.`;
    commit(autoBody, logBody, null);
  }

  function handleModify() {
    const changes = [
      mDrug !== med   && `Drug: ${mDrug}`,
      mStrength       && `Strength: ${mStrength}`,
      mSig            && `Directions: ${mSig}`,
                         `Qty: ${mQty}`,
                         `Refills: ${mRefills}`,
      mDaw            && "Dispense as Written (no substitution)",
    ].filter(Boolean).join(", ");
    const noteToPatient = mNote.trim() ? `\n\n${mNote.trim()}` : "";
    const autoBody = `Your refill request has been approved with the following changes: ${changes}. Sent to ${mPharma}. Please allow 24–48 hours for processing.${noteToPatient}`;
    const logBody  = `Refill modified for ${med} via patient portal message on ${nowStr}. Changes: ${changes}. Pharmacy: ${mPharma}. Authorized by ${PROVIDER}.`;
    commit(autoBody, logBody, { medication: mDrug, strength: mStrength, sig: mSig, quantity: mQty, refills: mRefills, pharmacy: mPharma, daw: mDaw, status: "active", prescribed_by: "dr-walker" });
  }

  const inSt  = { width: "100%", boxSizing: "border-box", background: C.navy3, border: `1px solid ${C.border2}`, borderRadius: 5, color: C.text1, fontSize: 12, padding: "5px 8px", outline: "none", fontFamily: "inherit" };
  const lblSt = { fontSize: 11, color: C.text3, marginBottom: 3, display: "block" };
  const fldSt = { marginBottom: 10 };
  const cancelBtn = { padding: "6px 14px", borderRadius: 5, background: C.navy4, border: `1px solid ${C.border2}`, color: C.text2, fontSize: 12, cursor: "pointer" };

  return (
    <>
      {/* ── Refill action bar ──────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${C.amber}33`, background: `${C.navy2}`, flexShrink: 0, padding: "10px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.amber }}>💊 Refill Action Required</span>
          <span style={{ fontSize: 11, background: C.amberLt, color: C.amber, borderRadius: 3, padding: "1px 8px", fontWeight: 600 }}>
            {med}
          </span>
        </div>

        {/* Controlled substance warning */}
        {controlled && (
          <div style={{ background: C.amberLt, border: `1px solid rgba(240,165,0,0.3)`, borderRadius: 6, padding: "8px 12px", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 5 }}>
              ⚠️ Controlled Substance — PDMP check recommended before approving
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: C.text2 }}>
                <input type="checkbox" checked={pdmpChecked} onChange={e => setPdmpChecked(e.target.checked)} style={{ accentColor: C.amber }} />
                I have reviewed the PDMP for this patient
              </label>
              <a href="https://www.narxcare.com" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: C.teal, textDecoration: "underline", whiteSpace: "nowrap" }}>
                Open NarxCare ↗
              </a>
            </div>
          </div>
        )}

        {/* Three action buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setPanel("approve")} disabled={!canApprove}
            style={{ flex: 1, padding: "7px 0", borderRadius: 5, background: canApprove ? C.greenLt : C.navy4, border: `1px solid ${canApprove ? C.green : C.border}`, color: canApprove ? C.green : C.text3, fontSize: 12, fontWeight: 600, cursor: canApprove ? "pointer" : "not-allowed" }}>
            ✅ Approve Refill
          </button>
          <button onClick={() => setPanel("deny")}
            style={{ flex: 1, padding: "7px 0", borderRadius: 5, background: C.redLt, border: `1px solid ${C.red}`, color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            ❌ Deny Refill
          </button>
          <button onClick={() => setPanel("modify")} disabled={!canApprove}
            style={{ flex: 1, padding: "7px 0", borderRadius: 5, background: canApprove ? C.amberLt : C.navy4, border: `1px solid ${canApprove ? C.amber : C.border}`, color: canApprove ? C.amber : C.text3, fontSize: 12, fontWeight: 600, cursor: canApprove ? "pointer" : "not-allowed" }}>
            ✏️ Approve with Modifications
          </button>
        </div>
      </div>

      {/* ── Approve modal ─────────────────────────────────────────────────────── */}
      {panel === "approve" && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div style={{ background: C.navy2, border: `1px solid ${C.border2}`, borderRadius: 10, padding: 20, width: 380, boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.green, marginBottom: 14 }}>✅ Approve Refill</div>
            <div style={fldSt}>
              <span style={lblSt}>Patient</span>
              <div style={{ fontSize: 13, color: C.text1, fontWeight: 600 }}>{thread.patient_name}</div>
            </div>
            <div style={fldSt}>
              <label style={lblSt}>Medication</label>
              <input style={inSt} value={aMed} onChange={e => setAMed(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lblSt}>Quantity</label>
                <input style={inSt} value={aQty} onChange={e => setAQty(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lblSt}>Refills</label>
                <input style={inSt} value={aRefills} onChange={e => setARefills(e.target.value)} />
              </div>
            </div>
            <div style={fldSt}>
              <label style={lblSt}>Pharmacy</label>
              <input style={inSt} value={aPharma} onChange={e => setAPharma(e.target.value)} />
            </div>
            <div style={{ background: C.greenLt, border: `1px solid rgba(34,197,94,0.2)`, borderRadius: 6, padding: "8px 10px", fontSize: 11, color: C.text2, marginBottom: 14 }}>
              Auto-reply: "Your refill for <b style={{ color: C.text1 }}>{aMed}</b> has been approved and sent to <b style={{ color: C.text1 }}>{aPharma}</b>."
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setPanel(null)} style={cancelBtn}>Cancel</button>
              <button onClick={handleApprove} style={{ padding: "6px 16px", borderRadius: 5, background: C.green, border: "none", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Confirm &amp; Send to Pharmacy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Deny modal ────────────────────────────────────────────────────────── */}
      {panel === "deny" && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div style={{ background: C.navy2, border: `1px solid ${C.border2}`, borderRadius: 10, padding: 20, width: 380, boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.red, marginBottom: 14 }}>❌ Deny Refill</div>
            <div style={fldSt}>
              <label style={lblSt}>Reason</label>
              <select value={dReason} onChange={e => setDReason(e.target.value)} style={{ ...inSt, cursor: "pointer" }}>
                {DENY_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={fldSt}>
              <label style={lblSt}>{dReason === "Other" ? "Reason (required)" : "Additional message to patient (optional)"}</label>
              <textarea rows={3} value={dNote} onChange={e => setDNote(e.target.value)}
                placeholder={dReason === "Other" ? "Enter denial reason..." : "Additional context for the patient..."}
                style={{ ...inSt, resize: "vertical", lineHeight: 1.5 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setPanel(null)} style={cancelBtn}>Cancel</button>
              <button onClick={handleDeny} disabled={dReason === "Other" && !dNote.trim()}
                style={{ padding: "6px 16px", borderRadius: 5, background: (dReason === "Other" && !dNote.trim()) ? C.navy4 : C.red, border: "none", color: (dReason === "Other" && !dNote.trim()) ? C.text3 : "#fff", fontSize: 12, fontWeight: 700, cursor: (dReason === "Other" && !dNote.trim()) ? "not-allowed" : "pointer" }}>
                Send Denial
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modify modal ──────────────────────────────────────────────────────── */}
      {panel === "modify" && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div style={{ background: C.navy2, border: `1px solid ${C.border2}`, borderRadius: 10, padding: 20, width: 420, boxShadow: "0 8px 32px rgba(0,0,0,0.5)", maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.amber, marginBottom: 14 }}>✏️ Approve with Modifications</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={lblSt}>Drug Name</label>
                <input style={inSt} value={mDrug} onChange={e => setMDrug(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lblSt}>Strength</label>
                <input style={inSt} value={mStrength} onChange={e => setMStrength(e.target.value)} placeholder="e.g. 5mg" />
              </div>
            </div>
            <div style={fldSt}>
              <label style={lblSt}>Sig / Directions</label>
              <input style={inSt} value={mSig} onChange={e => setMSig(e.target.value)} placeholder="e.g. Take 1 tablet by mouth twice daily" />
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lblSt}>Quantity</label>
                <input style={inSt} value={mQty} onChange={e => setMQty(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lblSt}>Refills</label>
                <input style={inSt} value={mRefills} onChange={e => setMRefills(e.target.value)} />
              </div>
            </div>
            <div style={fldSt}>
              <label style={lblSt}>Pharmacy</label>
              <input style={inSt} value={mPharma} onChange={e => setMPharma(e.target.value)} />
            </div>
            <div style={fldSt}>
              <label style={{ ...lblSt, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}>
                <input type="checkbox" checked={mDaw} onChange={e => setMDaw(e.target.checked)} style={{ accentColor: C.amber }} />
                <span>DAW — Dispense as Written (no substitution)</span>
              </label>
            </div>
            <div style={fldSt}>
              <label style={lblSt}>Note to patient (optional)</label>
              <textarea rows={2} value={mNote} onChange={e => setMNote(e.target.value)}
                placeholder="Any additional message..."
                style={{ ...inSt, resize: "vertical", lineHeight: 1.5 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setPanel(null)} style={cancelBtn}>Cancel</button>
              <button onClick={handleModify} style={{ padding: "6px 16px", borderRadius: 5, background: C.amber, border: "none", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Send Modified Refill
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: C.text3,
        textTransform: "uppercase", letterSpacing: "0.08em",
        marginBottom: 6, paddingBottom: 4,
        borderBottom: `1px solid ${C.border}`,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function PatientContextPanel({ thread, patients, onOpenChart, onAssign, onNavigate }) {
  if (!thread) {
    return (
      <div style={{
        width: 260, borderLeft: `1px solid ${C.border}`,
        background: C.navy2, display: "flex", alignItems: "center",
        justifyContent: "center", color: C.text3, fontSize: 12,
        flexShrink: 0,
      }}>
        No patient selected
      </div>
    );
  }

  const pt = (patients || []).find(p => p.id === thread.patient_id) || {};
  const meds    = (isDemoMode ? (MOCK_PT_MEDS[thread.patient_id]  || []) : []).slice(0, 5);
  const notes   = (isDemoMode ? (MOCK_PT_NOTES[thread.patient_id] || []) : []).slice(0, 2);
  const problems = (pt.problems || ["No active problems on file"]).slice(0, 5);
  const allergies = pt.allergies || ["NKDA"];
  const problemColors = [C.red, C.amber, C.blue, C.purple, C.teal];

  return (
    <div style={{
      width: 260, borderLeft: `1px solid ${C.border}`,
      background: C.navy2, overflowY: "auto", flexShrink: 0,
    }}>
      <div style={{ padding: "14px 14px 0" }}>
        {/* Patient avatar and info */}
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <PatAvatar name={thread.patient_name} size={48} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text1 }}>{thread.patient_name}</div>
          <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
            {thread.dob ? `${fmtDob(thread.dob)} · ${calcAge(thread.dob)} y/o` : ""}
          </div>
          <div style={{ fontSize: 11, color: C.text3 }}>MRN: {thread.mrn}</div>
          {pt.insurance && (
            <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{pt.insurance}</div>
          )}
          <button
            onClick={() => onOpenChart && onOpenChart(thread.patient_id)}
            style={{
              marginTop: 8, width: "100%", padding: "6px",
              background: C.tealLt, border: `1px solid ${C.teal}`,
              borderRadius: 5, color: C.teal, fontSize: 11,
              fontWeight: 600, cursor: "pointer",
            }}
          >Open Full Chart →</button>
          {!pt.id && onNavigate && (
            <button
              onClick={() => onNavigate("new-patient", { intakeMethod: "portal" })}
              style={{
                marginTop: 6, width: "100%", padding: "6px",
                background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.4)",
                borderRadius: 5, color: "#f59e0b", fontSize: 11,
                fontWeight: 600, cursor: "pointer",
              }}
            >📋 Create Chart from Intake</button>
          )}
        </div>

        {/* Assigned To */}
        <Section label="Assigned To">
          <select
            value={thread.assigned_to || ""}
            onChange={e => onAssign && onAssign(thread.id, e.target.value || null)}
            style={{
              width: "100%", background: C.navy3, border: `1px solid ${C.border2}`,
              borderRadius: 5, color: thread.assigned_to ? C.text1 : C.text3,
              fontSize: 12, padding: "5px 8px", outline: "none", cursor: "pointer",
            }}
          >
            <option value="">— Unassigned —</option>
            {STAFF_LIST.filter(s => s.id !== "practice").map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </Section>

        {/* Upcoming Appointments */}
        <Section label="Upcoming Appointments">
          {pt.nextAppt ? (
            <div style={{
              fontSize: 11, color: C.text2, background: C.navy3,
              borderRadius: 5, padding: "6px 8px",
            }}>{pt.nextAppt}</div>
          ) : (
            <div style={{ fontSize: 11, color: C.text3 }}>No upcoming appointments</div>
          )}
        </Section>

        {/* Active Problems */}
        <Section label="Active Problems">
          {problems.length === 0
            ? <div style={{ fontSize: 11, color: C.text3 }}>None on file</div>
            : problems.map((prob, i) => (
              <div key={i} style={{
                fontSize: 11, color: C.text2, marginBottom: 4,
                borderLeft: `2px solid ${problemColors[i % problemColors.length]}`,
                paddingLeft: 6,
              }}>{strVal(prob)}</div>
            ))
          }
        </Section>

        {/* Active Medications */}
        <Section label="Active Medications">
          {meds.length === 0
            ? <div style={{ fontSize: 11, color: C.text3 }}>None on file</div>
            : meds.map((med, i) => (
              <div key={i} style={{
                fontSize: 11, color: C.text2, marginBottom: 3,
                padding: "3px 0", borderBottom: i < meds.length - 1 ? `1px solid ${C.border}` : "none",
              }}>💊 {strVal(med)}</div>
            ))
          }
        </Section>

        {/* Allergies */}
        <Section label="Allergies">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {allergies.map((a, i) => (
              <span key={i} style={{
                fontSize: 10, background: C.redLt, color: C.red,
                borderRadius: 3, padding: "2px 6px", fontWeight: 600,
              }}>{strVal(a)}</span>
            ))}
          </div>
        </Section>

        {/* Recent Notes */}
        <Section label="Recent Notes">
          {notes.length === 0
            ? <div style={{ fontSize: 11, color: C.text3 }}>No recent notes</div>
            : notes.map((note, i) => (
              <div key={i} style={{
                background: C.navy3, borderRadius: 5, padding: "7px 8px",
                marginBottom: 6, border: `1px solid ${C.border}`,
              }}>
                <div style={{ fontSize: 10, color: C.text3, marginBottom: 2 }}>
                  {note.date} &nbsp;·&nbsp; <span style={{ color: C.teal }}>{note.type}</span>
                </div>
                <div style={{ fontSize: 11, color: C.text2, lineHeight: 1.4 }}>{note.summary}</div>
              </div>
            ))
          }
        </Section>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function PatientInboxPage({ patients = [], onPatientSelect, onNavigate }) {
  const { practiceId } = useApp() || {};
  const [messages, setMessages]         = useState([]);
  const [selected, setSelected]         = useState(null);
  const [filterTab, setFilterTab]       = useState("all");
  const [filterType, setFilterType]     = useState("");
  const [filterAssigned, setFilterAssigned] = useState("");
  const [search, setSearch]             = useState("");
  const [sortOrder, setSortOrder]       = useState("newest");

  // Load messages from Cloud SQL (db-query) or fallback to mock.
  // SECURITY FIX (Group C 2026-06-13): this read previously had NO practice_id
  // filter — on RLS-off Cloud SQL that is a cross-practice leak. It now scopes to
  // the JWT practice via .eq('practice_id', practiceId) (db-query ALSO force-ANDs
  // it, but the explicit filter keeps the call site honest + demo/Supabase-path
  // correct). NOTE: db-query's allowlist rejects PostgREST column aliases, so the
  // `type:message_type` alias is gone — we select message_type and map it to
  // `type` (the field the rest of this component reads) in JS.
  useEffect(() => {
    if (!practiceId && !isDemoMode) { setMessages([]); return; }
    let active = true;
    async function load() {
      try {
        let q = db
          .from("portal_messages")
          .select("id, patient_id, subject, message_type, body, sender_type, assigned_to, is_read, flagged_urgent, flag_reason, responded_at, responded_by, created_at, practice_id");
        if (practiceId) q = q.eq("practice_id", practiceId);
        const { data, error } = await q
          .order("created_at", { ascending: false })
          .limit(100);
        if (!error && data && data.length > 0 && active) {
          setMessages(data.map(m => ({ ...m, type: m.message_type })));
        } else {
          if (active) setMessages(isDemoMode ? MOCK_PORTAL_INBOX : []);
        }
      } catch {
        if (active) setMessages(isDemoMode ? MOCK_PORTAL_INBOX : []);
      }
    }
    load();
    return () => { active = false; };
  }, [practiceId]);

  const unreadCount  = useMemo(() => messages.filter(m => !m.is_read && m.status !== "archived").length, [messages]);
  const overdueCount = useMemo(() => messages.filter(m => isOverdue(m)).length, [messages]);
  const flaggedCount = useMemo(() => messages.filter(m => m.flagged_urgent && m.status !== "archived").length, [messages]);

  const filtered = useMemo(() => {
    let list = [...messages];

    // Tab filter
    if (filterTab === "all")       list = list.filter(m => m.status !== "archived");
    else if (filterTab === "unread")  list = list.filter(m => !m.is_read);
    else if (filterTab === "overdue") list = list.filter(m => isOverdue(m));
    else if (filterTab === "flagged") list = list.filter(m => m.flagged_urgent);
    else if (filterTab === "archived") list = list.filter(m => m.status === "archived");

    // Type filter
    if (filterType) list = list.filter(m => m.type === filterType);

    // Assigned filter
    if (filterAssigned === "__unassigned__") list = list.filter(m => !m.assigned_to);
    else if (filterAssigned) list = list.filter(m => m.assigned_to === filterAssigned);

    // Search
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(m =>
      (m.patient_name || "").toLowerCase().includes(q) ||
      (m.subject || "").toLowerCase().includes(q)
    );

    // Sort
    if (sortOrder === "newest") {
      list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortOrder === "oldest") {
      list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortOrder === "overdue-first") {
      list.sort((a, b) => {
        const ao = isOverdue(a) ? 0 : 1, bo = isOverdue(b) ? 0 : 1;
        if (ao !== bo) return ao - bo;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    }
    return list;
  }, [messages, filterTab, filterType, filterAssigned, search, sortOrder]);

  const handleSelect = (msg) => {
    setSelected(msg);
    if (!msg.is_read) {
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
      setSelected(s => s && s.id === msg.id ? { ...s, is_read: true } : s);
    }
  };

  const handleUpdate = (updated) => {
    setMessages(prev => prev.map(m => m.id === updated.id ? updated : m));
    setSelected(updated);
    // Sync to Cloud SQL (best-effort). Group C: the old .upsert(updated) carried
    // the whole in-memory object (incl. derived `type`/`patient_name`/`status`,
    // which are NOT columns) and always referenced an EXISTING row — converted to
    // .update(stripped).eq('id', …) (LabsPage precedent). Strip to the writable
    // set (message_type is the real column behind the `type` alias).
    if (!updated?.id) return;
    const patch = {};
    for (const k of PORTAL_MESSAGE_WRITABLE) {
      if (updated[k] !== undefined) patch[k] = updated[k];
    }
    if (updated.type !== undefined && patch.message_type === undefined) patch.message_type = updated.type;
    (async () => {
      try { await db.from("portal_messages").update(patch).eq("id", updated.id); } catch {}
    })();
  };

  const handleAssign = (msgId, staffId) => {
    const msg = messages.find(m => m.id === msgId);
    if (!msg) return;
    handleUpdate({ ...msg, assigned_to: staffId });
  };

  const TABS = [
    { id: "all",      label: "All" },
    { id: "unread",   label: "Unread",  badge: unreadCount  },
    { id: "overdue",  label: "Overdue", badge: overdueCount },
    { id: "flagged",  label: "Flagged", badge: flaggedCount },
    { id: "archived", label: "Archived" },
  ];

  const btnSort = (id, label) => (
    <button
      key={id}
      onClick={() => setSortOrder(id)}
      style={{
        padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600,
        border: `1px solid ${sortOrder === id ? C.teal : C.border2}`,
        background: sortOrder === id ? C.tealLt : "transparent",
        color: sortOrder === id ? C.teal : C.text3,
        cursor: "pointer",
      }}
    >{label}</button>
  );

  return (
    <div style={{
      display: "flex",
      height: "calc(100vh - var(--header-h, 60px))",
      overflow: "hidden",
      margin: "-24px",
      background: C.navy,
    }}>
      {/* ── Left Panel ─────────────────────────────────────────── */}
      <div style={{
        width: 280, flexShrink: 0, display: "flex", flexDirection: "column",
        borderRight: `1px solid ${C.border}`, background: C.navy2,
      }}>
        {/* Header */}
        <div style={{ padding: "14px 14px 10px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text1, marginBottom: 8 }}>
            Patient Messages
          </div>
          <input
            type="text"
            placeholder="Search patients or subjects..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%", boxSizing: "border-box",
              background: C.navy3, border: `1px solid ${C.border2}`,
              borderRadius: 6, color: C.text1, fontSize: 12,
              padding: "6px 10px", outline: "none",
            }}
          />
        </div>

        {/* Tabs */}
        <div style={{
          display: "flex", borderBottom: `1px solid ${C.border}`,
          overflowX: "auto", flexShrink: 0,
        }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilterTab(tab.id)}
              style={{
                flex: 1, padding: "7px 4px", fontSize: 11, fontWeight: 600,
                background: "transparent", border: "none",
                borderBottom: `2px solid ${filterTab === tab.id ? C.teal : "transparent"}`,
                color: filterTab === tab.id ? C.teal : C.text3,
                cursor: "pointer", whiteSpace: "nowrap",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span style={{
                  background: C.red, color: "#fff", borderRadius: 8,
                  fontSize: 9, fontWeight: 700, padding: "1px 4px", lineHeight: 1.3,
                }}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              style={{
                flex: 1, background: C.navy3, border: `1px solid ${C.border2}`,
                borderRadius: 5, color: filterType ? C.text1 : C.text3,
                fontSize: 11, padding: "4px 6px", outline: "none", cursor: "pointer",
              }}
            >
              <option value="">All Types</option>
              {MSG_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
            </select>
            <select
              value={filterAssigned}
              onChange={e => setFilterAssigned(e.target.value)}
              style={{
                flex: 1, background: C.navy3, border: `1px solid ${C.border2}`,
                borderRadius: 5, color: filterAssigned ? C.text1 : C.text3,
                fontSize: 11, padding: "4px 6px", outline: "none", cursor: "pointer",
              }}
            >
              <option value="">All Staff</option>
              <option value="__unassigned__">Unassigned</option>
              {STAFF_LIST.filter(s => s.id !== "practice").map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {btnSort("newest", "Newest")}
            {btnSort("oldest", "Oldest")}
            {btnSort("overdue-first", "Overdue First")}
          </div>
        </div>

        {/* Message list */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: C.text3, fontSize: 12 }}>
              No messages match the current filters.
            </div>
          ) : (
            filtered.map(msg => (
              <div key={msg.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                <InboxRow
                  msg={msg}
                  selected={selected?.id === msg.id}
                  onClick={() => handleSelect(msg)}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Center: Thread ──────────────────────────────────────── */}
      <ThreadView
        thread={selected}
        patients={patients}
        onUpdate={handleUpdate}
      />

      {/* ── Right: Patient Context ──────────────────────────────── */}
      <PatientContextPanel
        thread={selected}
        patients={patients}
        onOpenChart={(pid) => onPatientSelect && onPatientSelect(pid)}
        onAssign={handleAssign}
        onNavigate={onNavigate}
      />
    </div>
  );
}
